import { useState, type ReactNode } from "react";

interface FilterPanelProps {
  genres: string[];
  regions: string[];
  years: number[];
  selectedGenres: string[];
  selectedRegions: string[];
  selectedYears: number[];
  minRating: number;
  onToggleGenre: (value: string) => void;
  onToggleRegion: (value: string) => void;
  onToggleYear: (value: number) => void;
  onSetRating: (value: number) => void;
  onReset: () => void;
}

function Chip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
        active
          ? "bg-primary-500 text-background-50"
          : "bg-background-100 text-foreground-600 hover:bg-background-200"
      }`}
    >
      {label}
    </button>
  );
}

function FilterRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-200 py-3.5 last:border-b-0 sm:flex-row sm:items-start">
      <span className="w-full shrink-0 pt-1 text-xs font-semibold text-foreground-500 sm:w-16">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export default function FilterPanel({
  genres,
  regions,
  years,
  selectedGenres,
  selectedRegions,
  selectedYears,
  minRating,
  onToggleGenre,
  onToggleRegion,
  onToggleYear,
  onSetRating,
  onReset,
}: FilterPanelProps) {
  const [open, setOpen] = useState(false);
  const activeCount =
    selectedGenres.length + selectedRegions.length + selectedYears.length + (minRating > 1 ? 1 : 0);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex items-center justify-between px-4 py-3 lg:hidden">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="flex items-center gap-2 text-sm font-semibold text-foreground-800 cursor-pointer"
        >
          <i className="ri-filter-3-line text-accent-600"></i>
          筛选条件
          {activeCount > 0 && (
            <span className="rounded-full bg-accent-500 px-2 py-0.5 text-[11px] font-bold text-background-50">
              {activeCount}
            </span>
          )}
        </button>
        <i className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-lg text-foreground-500`}></i>
      </div>

      <div className={`${open ? "block" : "hidden"} px-4 pb-2 lg:block lg:p-5`}>
        <div className="mb-1 hidden items-center justify-between lg:flex">
          <span className="text-sm font-bold text-foreground-900">
            <i className="ri-filter-3-line mr-1 text-accent-600"></i>
            筛选条件
          </span>
          <button
            type="button"
            onClick={onReset}
            className="text-xs text-foreground-500 transition-colors hover:text-accent-600 cursor-pointer"
          >
            重置筛选
          </button>
        </div>

        <FilterRow label="类型">
          {genres.map((genre) => (
            <Chip
              key={genre}
              label={genre}
              active={selectedGenres.includes(genre)}
              onClick={() => onToggleGenre(genre)}
            />
          ))}
        </FilterRow>

        <FilterRow label="地区">
          {regions.map((region) => (
            <Chip
              key={region}
              label={region}
              active={selectedRegions.includes(region)}
              onClick={() => onToggleRegion(region)}
            />
          ))}
        </FilterRow>

        <FilterRow label="年份">
          {years.map((year) => (
            <Chip
              key={year}
              label={`${year}`}
              active={selectedYears.includes(year)}
              onClick={() => onToggleYear(year)}
            />
          ))}
        </FilterRow>

        <FilterRow label="评分">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => onSetRating(minRating === star ? 1 : star)}
              className={`flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                minRating >= star
                  ? "bg-secondary-100 text-secondary-800"
                  : "bg-background-100 text-foreground-500 hover:bg-background-200"
              }`}
            >
              <i className="ri-star-fill text-secondary-500"></i>
              {star}星以上
            </button>
          ))}
        </FilterRow>

        <div className="pt-3 lg:hidden">
          <button
            type="button"
            onClick={onReset}
            className="w-full rounded-md border border-background-300 py-2 text-sm text-foreground-600 cursor-pointer"
          >
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}